import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, Loader2 } from 'lucide-react';

import { subscriptionsApi, type SubscriptionPlan } from '@/lib/api';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { useSubscriptionCheckout } from '@/hooks/useSubscriptionCheckout';
import { useSubscriptionRenewal } from '@/hooks/useSubscriptionRenewal';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/components/AuthProvider';
import { buildPlanFeatureBullets } from '@/lib/billing/planFeatureBullets';
import { DemoLanguageMenu } from '@/components/onboarding-v2/DemoLanguageMenu';
import { CustomerReviewCarousel } from '@/components/billing/CustomerReviewCarousel';
import { cn } from '@/lib/utils';

/**
 * THE plan-expiry gate — one modal for every way a workspace loses access
 * (trial lapsed, subscription lapsed, cancelled, never had a plan), plus the
 * "Choose a plan" sheet it opens. See EXPIRY_COPY for what differs per kind.
 *
 * Trigger and state live in AppShell (`showBillingDialog`), the same switch that
 * drives BillingDialog, so there is exactly ONE expiry modal at a time. All
 * money flows through `useSubscriptionCheckout` — no gateway logic here.
 */

/** Public pricing page — the full feature-comparison matrix lives here. */
const COMPARE_PLANS_URL = 'https://wenext.ai/pricing';

/**
 * 🔴 HOTFIX (2026-10-24): self-serve checkout is OFF. Every expired gate —
 * trial or paid — shows only "Talk to Sales" and "Log out"; the plan / payment
 * CTAs are hidden. Flip to true to bring the plan picker and payment back.
 */
const SELF_SERVE_CHECKOUT = false;

const INTERVAL_ORDER = ['quarterly', 'half_yearly', 'yearly', 'monthly'] as const;

const INTERVAL_META: Record<string, { label: string; periodLabel: string; badge?: string }> = {
  monthly: { label: 'Monthly', periodLabel: '/ month' },
  quarterly: { label: 'Quarterly', periodLabel: '/ quarter' },
  half_yearly: { label: 'Half yearly', periodLabel: '/ 6 months' },
  yearly: { label: 'Yearly', periodLabel: '/ year', badge: 'Best value' },
};

function intervalLabel(interval: string): string {
  return (
    INTERVAL_META[interval]?.label ||
    interval.charAt(0).toUpperCase() + interval.slice(1).replace(/_/g, ' ')
  );
}

/**
 * Plans are stored one row per billing period, so their names carry the period
 * ("Launch Yearly", "Sprint Quarterly"). The cycle tab and the price suffix
 * already say the period, so strip it for display — otherwise a card under the
 * Yearly tab reads "Subscribe to Launch Yearly". Tier-only names are untouched.
 */
function displayPlanName(name: string): string {
  const trimmed = (name || '').trim();
  const stripped = trimmed.replace(/\s+(quarterly|half[\s-]?yearly|yearly|monthly)$/i, '').trim();
  return stripped || trimmed;
}

const CURRENCY_NAME: Record<string, string> = {
  INR: 'Indian Rupees',
  USD: 'US Dollars',
  EUR: 'Euros',
  GBP: 'Pounds Sterling',
};

function currencySymbol(currency?: string | null): string {
  const normalized = (currency || 'INR').toUpperCase();
  if (normalized === 'USD') return '$';
  if (normalized === 'EUR') return '€';
  if (normalized === 'GBP') return '£';
  return '₹';
}

function formatAmount(price: string | number, currency?: string | null): string {
  const amount = Number(price);
  if (!Number.isFinite(amount)) return `${currencySymbol(currency)}${price}`;
  return `${currencySymbol(currency)}${amount.toLocaleString('en-IN', {
    maximumFractionDigits: 0,
  })}`;
}

function formatTrialDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function daysBetween(from: Date, to: Date): number {
  return Math.ceil((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000));
}

/**
 * Which way a workspace lost access. ONE modal serves all four so the shell,
 * the plan sheet and the exit actions can never drift apart between them; only
 * the wording and three optional blocks differ.
 */
export type PlanExpiryKind = 'trial' | 'subscription' | 'cancelled' | 'no_plan';

/** Secondary actions under the CTA, rendered in this order. */
type ExpiryAction = 'choose_plan' | 'talk_to_sales' | 'logout';

interface ExpiryCopy {
  /** Used when the plan name is unknown — the heading must still read sensibly. */
  heading: string;
  /**
   * Preferred heading when we know which plan lapsed. Naming it beats a generic
   * "subscription": the user sees exactly what they lost, and it matches the
   * plan named on the period line and charged by the CTA.
   */
  headingWithPlan?: (planName: string) => string;
  /** Rendered around the organization name, which is emphasised between the two. */
  bodyBefore: string;
  bodyAfter: string;
  cta: string;
  /** The red "DAYS 0" countdown mark — a trial idea, meaningless for a paid plan. */
  showDaysBadge: boolean;
  /**
   * Which date range to print under the body:
   *   'trial' — "15-day trial · 5 Sep 2026 → 20 Sep 2026"
   *   'plan'  — "Launch Quarterly · 5 Sep 2026 → 20 Sep 2026"
   * A lapsed payer needs to see WHICH plan lapsed and when, since the CTA
   * renews that exact plan rather than opening the picker.
   */
  period: 'trial' | 'plan' | null;
  /**
   * 'plans'  — open the plan sheet to choose something.
   * 'renew'  — go straight to Razorpay for the plan they already had.
   */
  primaryAction: 'plans' | 'renew';
  actions: readonly ExpiryAction[];
}

/**
 * Wording follows the copy already shipped in TrialBanner.tsx rather than a new
 * voice, so the strip and the modal say the same thing about the same state.
 */
const EXPIRY_COPY: Record<PlanExpiryKind, ExpiryCopy> = {
  trial: {
    heading: 'Your trial has expired',
    bodyBefore:
      "You'll no longer be able to access inbox, campaigns, CRM, and other WeNext features for ",
    bodyAfter: '.',
    cta: 'Choose a plan',
    showDaysBadge: true,
    period: 'trial',
    primaryAction: 'plans',
    actions: ['talk_to_sales', 'logout'],
  },
  subscription: {
    heading: 'Your subscription has expired',
    headingWithPlan: (planName) => `Your ${planName} plan has expired`,
    bodyBefore: 'Renew to continue using inbox, campaigns, CRM, and other WeNext features for ',
    bodyAfter: '.',
    cta: 'Proceed to payment',
    // Same card as the trial variant, countdown included — only the wording and
    // the actions differ.
    showDaysBadge: true,
    period: 'plan',
    primaryAction: 'renew',
    actions: ['choose_plan', 'talk_to_sales', 'logout'],
  },
  cancelled: {
    heading: 'Your subscription was cancelled',
    bodyBefore: 'Pick a plan to restore inbox, campaigns, CRM, and other WeNext features for ',
    bodyAfter: '.',
    cta: 'Choose a plan',
    showDaysBadge: false,
    period: null,
    primaryAction: 'plans',
    actions: ['logout'],
  },
  no_plan: {
    heading: "You're not on a plan yet",
    bodyBefore: 'Select a plan to unlock all features for ',
    bodyAfter: '.',
    cta: 'Select a plan',
    showDaysBadge: false,
    period: null,
    primaryAction: 'plans',
    actions: ['logout'],
  },
};

export interface TrialExpiredGateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Defaults to 'trial' so existing callers keep their behaviour. */
  kind?: PlanExpiryKind;
}

export function TrialExpiredGate({ open, onOpenChange, kind = 'trial' }: TrialExpiredGateProps) {
  const copy = EXPIRY_COPY[kind];
  const [step, setStep] = useState<'expired' | 'plans'>('expired');
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const { data: subscription } = useCurrentSubscription();
  const { selectedWorkspace } = useWorkspaceContext();
  const { user, signOut } = useAuth();
  const { startCheckout } = useSubscriptionCheckout();
  const { startRenewal, renewing } = useSubscriptionRenewal();

  const {
    data: plans,
    isLoading: plansLoading,
    error: plansError,
  } = useQuery({
    // Shared cache key with the Billing page — one fetch, one source of truth.
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const result = await subscriptionsApi.getPlans();
      if (result.error) throw new Error(result.error);
      return result.data || [];
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  /** Sellable plans only: active, priced, and not tied to another storefront. */
  const sellablePlans = useMemo(
    () =>
      (plans || []).filter(
        (p) => p.is_active && !p.type && Number(p.price) > 0,
      ),
    [plans],
  );

  const availableIntervals = useMemo(() => {
    const present = new Set(sellablePlans.map((p) => p.billing_period));
    const ordered = INTERVAL_ORDER.filter((i) => present.has(i));
    const extras = [...present].filter(
      (i) => !INTERVAL_ORDER.includes(i as (typeof INTERVAL_ORDER)[number]),
    );
    return [...ordered, ...extras];
  }, [sellablePlans]);

  const [selectedInterval, setSelectedInterval] = useState<string | null>(null);
  const activeInterval =
    selectedInterval && availableIntervals.includes(selectedInterval)
      ? selectedInterval
      : availableIntervals.includes('yearly')
        ? 'yearly'
        : (availableIntervals[0] ?? null);

  const visiblePlans = useMemo(() => {
    if (!activeInterval) return [];
    return sellablePlans
      .filter((p) => p.billing_period === activeInterval)
      .sort((a, b) => {
        const order = (a.sort_order ?? 0) - (b.sort_order ?? 0);
        if (order !== 0) return order;
        return Number(a.price) - Number(b.price);
      });
  }, [sellablePlans, activeInterval]);

  /**
   * The highlighted tier. Derived from the data (the mid tier by sort order)
   * rather than a hardcoded plan name, so renaming a plan cannot break it.
   */
  const popularPlanId = useMemo(() => {
    if (visiblePlans.length < 2) return null;
    const midIndex = Math.min(1, visiblePlans.length - 1);
    return visiblePlans[midIndex]?.id ?? null;
  }, [visiblePlans]);

  /**
   * The reassurance row under the cards. The billing line is derived from the
   * plans actually on screen rather than hardcoded to INR — the plan table also
   * holds USD rows (the Shopify tier), and promising "Indian Rupees" beside a
   * dollar price would be a false statement about money.
   */
  const reassurances = useMemo(() => {
    const currency = (visiblePlans[0]?.currency || 'INR').toUpperCase();
    return [
      { title: 'Cancel anytime', body: 'No long-term lock-in on paid plans.' },
      {
        title: `${currency} billing`,
        body: `Prices shown in ${CURRENCY_NAME[currency] || currency}, tax as applicable.`,
      },
      { title: 'Support included', body: 'Every paid plan comes with support.' },
    ];
  }, [visiblePlans]);

  // The subscription belongs to the organization, so name the org (organizations.name),
  // not whichever workspace happens to be selected.
  const organizationName = user?.organization_name || selectedWorkspace?.name || 'your organization';
  const planName = subscription?.plan?.name || null;
  // Falls back to the generic heading whenever the plan name is unavailable.
  const heading = planName && copy.headingWithPlan ? copy.headingWithPlan(planName) : copy.heading;
  const periodStartLabel = formatTrialDate(subscription?.current_period_start);
  const periodEndLabel = formatTrialDate(subscription?.current_period_end);
  /** The plan the lapsed subscription was on — what 'renew' charges for. */
  const renewPlanId = subscription?.plan_id || subscription?.plan?.id || null;
  const trialStartLabel = formatTrialDate(subscription?.trial_start);
  const trialEndLabel = formatTrialDate(subscription?.trial_end);

  const totalTrialDays = useMemo(() => {
    if (subscription?.trial_days && subscription.trial_days > 0) return subscription.trial_days;
    if (subscription?.trial_start && subscription?.trial_end) {
      const days = daysBetween(
        new Date(subscription.trial_start),
        new Date(subscription.trial_end),
      );
      return days > 0 ? days : null;
    }
    return null;
  }, [subscription?.trial_days, subscription?.trial_start, subscription?.trial_end]);

  /**
   * Days left on whichever clock applies — the trial's, or the paid billing
   * period's. Both read 0 once lapsed, which is what the badge is there to say;
   * without the paid fallback a subscription card would always show 0 by
   * accident rather than by calculation.
   */
  const daysRemaining = useMemo(() => {
    const end = subscription?.trial_end || subscription?.current_period_end;
    if (!end) return 0;
    return Math.max(0, daysBetween(new Date(), new Date(end)));
  }, [subscription?.trial_end, subscription?.current_period_end]);

  if (!open) return null;

  const handleSubscribe = async (planId: string) => {
    if (pendingPlanId) return;
    setPendingPlanId(planId);
    try {
      await startCheckout(planId);
    } finally {
      setPendingPlanId(null);
    }
  };

  /*
   * signOut awaits two network calls (push unregister + logout) before it
   * clears the session and hard-redirects to /auth. The gate therefore STAYS
   * OPEN until that redirect — closing it first exposed the dashboard of an
   * expired account for the whole round-trip.
   */
  const handleLogout = () => {
    if (loggingOut) return;
    setLoggingOut(true);
    void signOut?.();
  };

  /**
   * The CTA either opens the picker or renews the plan they already had.
   *
   * 'renew' hands the existing plan id straight to the shared checkout, so the
   * gateway sheet opens on the first click — no plan re-selection. If the plan
   * id is somehow missing we fall back to the picker rather than dead-ending
   * the user on a button that cannot charge anything.
   */
  const handlePrimaryAction = async () => {
    // A manually paid plan renews by one-time payment — the same flow as the
    // banner, Plans and Billing pages (it keeps the same subscription and
    // continues its dates). Only when the server says renewal isn't open
    // (e.g. the plan was on auto-pay) does it fall back to a new checkout.
    if (copy.primaryAction === 'renew' && subscription?.renewal?.eligible) {
      await startRenewal();
      return;
    }
    if (copy.primaryAction === 'renew' && renewPlanId) {
      await handleSubscribe(renewPlanId);
      return;
    }
    setStep('plans');
  };

  /**
   * The public pricing page is the comparison surface — it carries the full
   * feature matrix, which the in-app plan sheet does not.
   *
   * Opened in a NEW TAB and the gate is deliberately left open: this is a
   * blocking modal on an expired account, so closing it would drop the user on
   * a dashboard they cannot use, with no way back to the plans.
   */
  const handleComparePlans = () => {
    window.open(COMPARE_PLANS_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      data-testid="trial-expired-gate"
      className={cn(
        'fixed inset-0 z-[100]',
        step === 'plans'
          ? 'flex items-end justify-stretch'
          : 'flex items-center justify-center p-4 sm:p-6',
      )}
    >
      {/* Dim overlay — the dashboard stays visible behind the gate. */}
      <div
        className="absolute inset-0 bg-[#101828]/55 backdrop-blur-[2px]"
        onClick={step === 'plans' ? () => setStep('expired') : undefined}
      />

      {step === 'expired' ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="trial-expired-title"
          className="relative z-10 flex w-full max-w-[440px] flex-col overflow-hidden rounded-2xl border border-[#E4E7EC] bg-white shadow-[0_24px_64px_rgba(16,24,40,0.28)]"
        >
          <div className="px-7 pb-6 pt-8 text-center sm:px-8">
            {/* Days-remaining calendar mark — a trial countdown, meaningless for a lapsed paid plan. */}
            {copy.showDaysBadge && (
              <div className="mx-auto mb-5 flex h-14 w-14 flex-col overflow-hidden rounded-xl border border-[#FECDCA] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06)]">
                <div className="flex h-4 items-center justify-center bg-[#F04438] text-[9px] font-bold uppercase tracking-wider text-white">
                  Days
                </div>
                <div className="flex flex-1 items-center justify-center text-[22px] font-semibold tabular-nums text-[#B42318]">
                  {daysRemaining}
                </div>
              </div>
            )}

            <h2
              id="trial-expired-title"
              className="text-[22px] font-semibold tracking-tight text-[#101828]"
            >
              {heading}
            </h2>
            <p className="mt-2 text-[14px] leading-relaxed text-[#667085]">
              {copy.bodyBefore}
              <span className="font-semibold text-[#101828]">{organizationName}</span>
              {copy.bodyAfter}
            </p>
            {copy.period === 'trial' && trialStartLabel && trialEndLabel && (
              <p data-testid="expiry-period" className="mt-2 text-[12.5px] text-[#667085]">
                {totalTrialDays ? `${totalTrialDays}-day trial · ` : ''}
                {trialStartLabel} → {trialEndLabel}
              </p>
            )}
            {copy.period === 'plan' && (planName || periodEndLabel) && (
              <p data-testid="expiry-period" className="mt-2 text-[12.5px] text-[#667085]">
                {planName && <span className="font-semibold text-[#344054]">{planName}</span>}
                {planName && periodStartLabel && periodEndLabel ? ' · ' : ''}
                {periodStartLabel && periodEndLabel
                  ? `${periodStartLabel} → ${periodEndLabel}`
                  : periodEndLabel
                    ? `Ended ${periodEndLabel}`
                    : ''}
              </p>
            )}
          </div>

          <CustomerReviewCarousel className="mx-5 mb-5 sm:mx-6" />

          <div className="border-t border-[#EAECF0] px-7 py-5 sm:px-8">
            {SELF_SERVE_CHECKOUT ? (
              <button
                type="button"
                data-testid="trial-expired-choose-plan"
                disabled={pendingPlanId !== null || renewing}
                onClick={() => void handlePrimaryAction()}
                className="inline-flex h-10 w-full select-none items-center justify-center gap-1.5 rounded-lg bg-[#0EB64F] text-[13.5px] font-semibold text-white transition hover:bg-[#0A8A3B] disabled:bg-[#98A2B3]"
              >
                {(pendingPlanId !== null || renewing) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {copy.cta}
              </button>
            ) : (
              <DemoLanguageMenu
                testIdPrefix="plan-expired-talk-to-sales"
                contentClassName="z-[110]"
                onSelect={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
              >
                <button
                  type="button"
                  data-testid="plan-expired-talk-to-sales"
                  className="inline-flex h-10 w-full select-none items-center justify-center gap-1.5 rounded-lg bg-[#0EB64F] text-[13.5px] font-semibold text-white transition hover:bg-[#0A8A3B]"
                >
                  Talk to Sales
                </button>
              </DemoLanguageMenu>
            )}
            {/*
              One centred row of secondary actions, ordered by the variant's
              `actions` list. Separators are interleaved rather than hardcoded so
              any combination reads correctly without a dangling "·".

              Talk to Sales' two destinations are the SAME ones behind the "Book
              a Live Demo" popup on wenext.ai; that popup keeps its open state in
              local React state with no URL trigger, so it cannot be deep-linked.
              Offering the identical language choice here lands the user on the
              same booking page one click sooner.
            */}
            {SELF_SERVE_CHECKOUT && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[13px] empty:mt-0">
              {copy.actions
                // Logout is not an alternative to paying — it gets its own line below.
                .filter((action) => action !== 'logout')
                .map((action) => {
                  switch (action) {
                    case 'choose_plan':
                      return (
                        <button
                          key="choose-plan"
                          type="button"
                          data-testid="plan-expired-choose-plan"
                          // Opens the in-app plan sheet — the only route a lapsed
                          // customer has to a DIFFERENT plan, since the CTA next
                          // to it renews the one they already had. The sheet's own
                          // footer still links out to the public pricing page.
                          onClick={() => setStep('plans')}
                          className="font-medium text-[#667085] transition hover:text-[#101828]"
                        >
                          Choose plan
                        </button>
                      );
                    case 'talk_to_sales':
                      return (
                        <DemoLanguageMenu
                          key="talk-to-sales"
                          testIdPrefix="plan-expired-talk-to-sales"
                          contentClassName="z-[110]"
                          onSelect={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
                        >
                          <button
                            type="button"
                            data-testid="plan-expired-talk-to-sales"
                            // Same muted secondary as "Choose plan" beside it: both are
                            // the quiet alternatives to the green Proceed CTA above.
                            className="font-medium text-[#667085] transition hover:text-[#101828]"
                          >
                            Talk to Sales
                          </button>
                        </DemoLanguageMenu>
                      );
                    default:
                      return null;
                  }
                })
                .filter(Boolean)
                .map((action, index) => (
                  <React.Fragment key={(action as React.ReactElement).key}>
                    {index > 0 && <span className="text-[#D0D5DD]">·</span>}
                    {action}
                  </React.Fragment>
                ))}
            </div>
            )}
            {(!SELF_SERVE_CHECKOUT || copy.actions.includes('logout')) && (
              <div className="mt-3 flex justify-center text-[13px]">
                <button
                  type="button"
                  data-testid="trial-expired-logout"
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="inline-flex items-center gap-1 font-medium text-[#667085] transition hover:text-[#101828] disabled:opacity-60"
                >
                  {loggingOut && <Loader2 className="h-3 w-3 animate-spin" />}
                  {loggingOut ? 'Logging out…' : 'Log out'}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="choose-plan-title"
          className="relative z-10 flex h-[90vh] w-full flex-col overflow-hidden rounded-b-none rounded-t-2xl border border-b-0 border-[#E4E7EC] bg-white shadow-[0_-12px_48px_rgba(16,24,40,0.2)]"
        >
          {/* Drag handle */}
          <div className="flex shrink-0 justify-center pb-1 pt-3">
            <span className="h-1 w-10 rounded-full bg-[#D0D5DD]" />
          </div>

          <div className="flex shrink-0 flex-col gap-3 border-b border-[#EAECF0] px-5 pb-4 pt-1 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  data-testid="choose-plan-back"
                  onClick={() => setStep('expired')}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#667085] transition hover:bg-[#F2F4F7] hover:text-[#101828]"
                  aria-label="Back"
                >
                  <ArrowRight className="h-4 w-4 rotate-180" />
                </button>
                <h2 id="choose-plan-title" className="text-[18px] font-semibold text-[#101828]">
                  Choose a plan
                </h2>
              </div>
              <p className="mt-1 text-[13px] text-[#667085] sm:pl-[2.625rem]">
                Upgrade to unlock {organizationName} and access all features.
              </p>
            </div>

            {availableIntervals.length > 1 && (
              <div
                role="tablist"
                aria-label="Billing cycle"
                className="flex max-w-full shrink-0 select-none items-center gap-1 self-start overflow-x-auto rounded-lg border border-[#E4E7EC] bg-[#F9FAFB] p-1 sm:self-auto"
              >
                {availableIntervals.map((id) => {
                  const active = id === activeInterval;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      data-testid={`billing-cycle-${id}`}
                      onClick={() => setSelectedInterval(id)}
                      className={cn(
                        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] font-semibold transition',
                        active
                          ? 'bg-white text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,0.08)]'
                          : 'text-[#667085] hover:text-[#101828]',
                      )}
                    >
                      {intervalLabel(id)}
                      {INTERVAL_META[id]?.badge && (
                        <span className="text-[11px] font-semibold text-[#076A2D]">
                          {INTERVAL_META[id]?.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-8">
            {plansLoading ? (
              <div className="flex h-40 items-center justify-center text-[#667085]">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : plansError ? (
              <p className="py-10 text-center text-[13px] text-[#B42318]">
                We couldn&apos;t load plans right now. Please try again shortly.
              </p>
            ) : visiblePlans.length === 0 ? (
              <p className="py-10 text-center text-[13px] text-[#667085]">
                No plans are available for this billing period.
              </p>
            ) : (
              <div
                className={cn(
                  'mx-auto grid max-w-[1100px] gap-3',
                  visiblePlans.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2',
                )}
              >
                {visiblePlans.map((plan: SubscriptionPlan) => {
                  const popular = plan.id === popularPlanId;
                  const { seatsLine, bullets } = buildPlanFeatureBullets(plan);
                  const busy = pendingPlanId === plan.id;
                  return (
                    <div
                      key={plan.id}
                      data-testid={`plan-card-${plan.slug || plan.id}`}
                      className={cn(
                        'relative flex flex-col rounded-xl border p-4',
                        popular
                          ? 'border-[#0EB64F] bg-[#F4FBF6] shadow-[0_0_0_1px_rgba(14,182,79,0.2)]'
                          : 'border-[#E4E7EC] bg-white',
                      )}
                    >
                      {popular && (
                        <span className="absolute -top-2.5 left-4 rounded-md bg-[#0EB64F] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                          Popular
                        </span>
                      )}
                      <p className="text-[15px] font-semibold text-[#101828]">{displayPlanName(plan.name)}</p>
                      {plan.description && (
                        <p className="mt-0.5 text-[12.5px] leading-snug text-[#667085]">
                          {plan.description}
                        </p>
                      )}
                      <p className="mt-2 flex items-baseline gap-1">
                        <span className="text-[24px] font-semibold tracking-tight text-[#101828]">
                          {formatAmount(plan.price, plan.currency)}
                        </span>
                        <span className="text-[12.5px] font-medium text-[#667085]">
                          {INTERVAL_META[plan.billing_period]?.periodLabel ||
                            `/ ${plan.billing_period.replace(/_/g, ' ')}`}
                        </span>
                      </p>
                      {seatsLine && (
                        <p className="mt-1 text-[12.5px] text-[#667085]">{seatsLine}</p>
                      )}
                      <ul className="mb-5 mt-4 flex-1 space-y-2 border-t border-[#EAECF0] pt-3">
                        {bullets.map((feature) => (
                          <li
                            key={feature}
                            className="flex items-start gap-2 text-[13px] leading-snug text-[#344054]"
                          >
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0EB64F]" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        data-testid={`subscribe-${plan.slug || plan.id}`}
                        disabled={busy}
                        onClick={() => void handleSubscribe(plan.id)}
                        className={cn(
                          'inline-flex h-9 w-full select-none items-center justify-center gap-1.5 rounded-lg text-[13px] font-semibold transition disabled:cursor-not-allowed',
                          popular
                            ? 'bg-[#0EB64F] text-white hover:bg-[#0A8A3B] disabled:bg-[#98A2B3]'
                            : 'border border-[#101828] bg-white text-[#101828] hover:bg-[#F9FAFB] disabled:border-[#E4E7EC] disabled:text-[#98A2B3]',
                        )}
                      >
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Subscribe to {displayPlanName(plan.name)}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {visiblePlans.length > 0 && (
              <div className="mx-auto mt-5 grid max-w-[1100px] gap-3 border-t border-[#EAECF0] pt-4 sm:grid-cols-3">
                {reassurances.map((item) => (
                  <div key={item.title} className="text-center sm:text-left">
                    <p className="text-[12.5px] font-semibold text-[#101828]">{item.title}</p>
                    <p className="mt-0.5 text-[12px] text-[#667085]">{item.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex shrink-0 justify-center border-t border-[#EAECF0] px-5 py-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))]">
            <button
              type="button"
              data-testid="choose-plan-compare"
              onClick={handleComparePlans}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#076A2D] transition hover:text-[#0A8A3B]"
            >
              Compare plans
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrialExpiredGate;
